import React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import {
  Login,
  Error,
  HomeLayout,
  Register,
  DashboardLayout,
  SettingsLayout,
  Bestellungen,
  Kartenverwaltung,
  Links,
  DashboardIntro,
  Erscheinungsbild,
  CardPage,
  VerifyEmail,
  ForgotPassword,
  ResetPassword,
  Produkte,
  Checkout,
  Linkvalidation,
} from './pages';

import {
  OrderPage,
  PaymentSuccess,
  ProductDetail,
  ProductGrid,
  ShoppingCart,
  ErrorElement,
  UserList,
  UserDetail,
  OrderList,
  OrderDetail,
  CardList,
  CardDetail,
} from './components';

import { action as registerAction } from './pages/Register';
import { action as loginAction } from './pages/Login';
import { action as linkEditAction } from './components/links/LinkEditSlideIn';
import { action as personalSettingsAction } from './components/dashboard/Settings';
import { action as linkSettingsAction } from './components/dashboard/erscheinungsbild/ErscheinungsbildForm';
import { action as forgotPasswordAction } from './pages/ForgotPassword';
import { action as resetPasswordAction } from './pages/ResetPassword';
import { action as orderAction } from './components/bestellungen/OrderPage';
import { action as customProductAction } from './components/produkte/ProductCustomizationSlideIn';
import { action as benutzerDetailAction } from './components/dashboard/admin/benutzerverwaltung/userDetail';
import { loader as DashboardLoader } from './pages/DashboardLayout';
import { loader as LinksLoader } from './pages/Links';
import { loader as publicLinksLoader } from './pages/CardPage';
import { loader as linkSettingsLoader } from './pages/Erscheinungsbild';
import { loader as verifyEmailLoader } from './pages/VerifyEmail';
import { loader as resetPasswordLoader } from './pages/ResetPassword';
import { loader as productsLoader } from './components/produkte/ProductGrid';
import { loader as singleProductLoader } from './components/produkte/ProductDetail';
import { loader as bestellungenLoader } from './components/bestellungen/OrderPage';
import { loader as settingsLoader } from './components/dashboard/Settings';
import { loader as orderLoader } from './pages/Bestellungen';
import { loader as cardsLoader } from './components/dashboard/kartenverwaltung/CardList';
import { loader as benutzerverwaltungsLoader } from './components/dashboard/admin/benutzerverwaltung/userList';
import { loader as benutzerDetailLoader } from './components/dashboard/admin/benutzerverwaltung/userDetail';
import { loader as orderListLoader } from './components/dashboard/admin/bestellbearbeitung/orderList';
import { loader as orderDetailLoader } from './components/dashboard/admin/bestellbearbeitung/OrderDetail';
import { loader as cardListLoader } from './components/dashboard/admin/kartenadministration/cardList';
import { loader as cardDetailLoader } from './components/dashboard/admin/kartenadministration/CardDetail';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
    },
  },
});

const getRedirectComponent = (path) => {
  // In Entwicklungsumgebung zeige nur Hinweis an, in Produktion leite weiter
  if (import.meta.env.DEV) {
    return () => (
      <div className="p-4 bg-yellow-100 border-l-4 border-yellow-500">
        <p>
          In production, this would redirect to: https://www.svisit.ch{path}
        </p>
        <button
          className="mt-2 bg-blue-500 text-white px-3 py-1 rounded-sm"
          onClick={() => window.open(`https://www.svisit.ch${path}`, '_blank')}
        >
          Visit URL
        </button>
      </div>
    );
  }

  return () => {
    useEffect(() => {
      window.location.href = `https://www.svisit.ch${path}`;
    }, []);
    return <p>Redirecting...</p>;
  };
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <HomeLayout />,
    errorElement: <Error />,
    children: [
      {
        index: true,
        element: React.createElement(getRedirectComponent('')),
      },
      {
        path: 'register',
        element: <Register />,
        action: registerAction,
      },
      {
        path: 'login',
        element: <Login />,
        action: loginAction(queryClient),
      },
      {
        path: '/ueber-uns',
        element: React.createElement(getRedirectComponent('/ueber-uns')),
      },
      {
        path: 'verify-email',
        element: <VerifyEmail />,
        loader: verifyEmailLoader,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
        action: forgotPasswordAction,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
        action: resetPasswordAction,
        loader: resetPasswordLoader,
      },
      {
        path: 'impressum',
        element: React.createElement(getRedirectComponent('/impressum')),
      },
      {
        path: 'datenschutz',
        element: React.createElement(getRedirectComponent('/datenschutz')),
      },
      {
        path: 'agb',
        element: React.createElement(getRedirectComponent('/agb')),
      },
    ],
  },
  {
    path: '/dashboard',
    element: <DashboardLayout queryClient={queryClient} />,
    loader: DashboardLoader(queryClient),
    children: [
      {
        index: true,
        element: <DashboardIntro />,
      },
      {
        path: 'links',
        element: <Links />,
        loader: LinksLoader(queryClient),
        action: linkEditAction(queryClient),
        errorElement: <ErrorElement />,
      },
      {
        path: 'kartenverwaltung',
        element: <Kartenverwaltung />,
        loader: cardsLoader(queryClient),
        errorElement: <ErrorElement />,
      },
      {
        path: 'bestellungen',
        element: <Bestellungen />,
        loader: orderLoader(queryClient),
        errorElement: <ErrorElement />,
      },
      {
        path: 'einstellungen',
        element: <SettingsLayout />,
        action: personalSettingsAction(queryClient),
        loader: settingsLoader(queryClient),
        errorElement: <ErrorElement />,
      },
      {
        path: 'erscheinungsbild',
        element: <Erscheinungsbild />,
        loader: linkSettingsLoader(queryClient),
        action: linkSettingsAction(queryClient),
        errorElement: <ErrorElement />,
      },
      {
        path: 'benutzerverwaltung',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <UserList />,
            loader: benutzerverwaltungsLoader(queryClient),
          },
          {
            path: ':id',
            loader: benutzerDetailLoader,
            action: benutzerDetailAction,
            element: <UserDetail />,
          },
        ],
      },
      {
        path: 'bestellbearbeitung',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <OrderList />,
            loader: orderListLoader(queryClient),
          },
          {
            path: ':id',
            loader: orderDetailLoader,
            element: <OrderDetail />,
          },
        ],
      },
      {
        path: 'kartenadministration',
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            element: <CardList />,
            loader: cardListLoader(queryClient),
          },
          {
            path: ':id',
            loader: cardDetailLoader,
            element: <CardDetail />,
          },
        ],
      },
    ],
  },
  {
    path: '/c/:id',
    element: <CardPage />,
    loader: publicLinksLoader,
  },
  {
    path: '/l/:id',
    element: <Linkvalidation />,
  },
  {
    path: '/produkte',
    element: <Produkte />,
    children: [
      {
        index: true,
        element: <ProductGrid />,
        loader: productsLoader,
      },
      {
        path: ':id',
        element: <ProductDetail />,
        loader: singleProductLoader,
        action: customProductAction,
      },
    ],
  },
  {
    path: '/warenkorb',
    element: <Checkout />,
    children: [
      {
        index: true,
        element: <ShoppingCart />,
      },
      {
        path: 'bestellung',
        element: <OrderPage />,
        loader: bestellungenLoader,
        action: orderAction,
      },
      {
        path: 'bezahlung',
        element: <PaymentSuccess />,
      },
    ],
  },
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
